import { CSSTransition, TransitionGroup } from 'react-transition-group'
import { CodeDesign } from '../GeneratorSections/CodeDesign/CodeDesign'
import { FC } from 'react'
import { LoggedOutGeneratorSection } from '../LoggedOutCodeGenerator.types'
import { SCROLL_CONTAINER_ID, TRANSITION_DURATION } from '../LoggedOutCodeGenerator.constants'
import { ScanDestination } from '../GeneratorSections/ScanDestination/ScanDestination'
import { Stack } from '@dtx-company/design-system/src'
import { useGlobalServices } from '@dtx-company/app/shared/common/src/hooks/useGlobalServices'
import { useLOCGSectionTransitionStyles } from './hooks/useLOCGSectionTransitionStyles'
import { useScrollableContainerState } from './hooks/useScrollableContainerState'
import { useSelector } from '@xstate/react'
import dynamic from 'next/dynamic'

const AuthSection = dynamic(
  () => import('../GeneratorSections/AuthSection/AuthSection').then(mod => mod.AuthSection),
  {
    ssr: false
  }
)

export const ScrollableContainer: FC = () => {
  const { locgService } = useGlobalServices()
  const { rootContainerRef, scanDestinationRef, codeDesignContainerRef, codeDesignRefs, authRef } =
    useScrollableContainerState()
  const displayScanDestination = useSelector(locgService, state => state.matches('scanDestination'))
  const displayCodeDesign = useSelector(locgService, state => state.matches('codeDesign'))
  const displayAuthForm = useSelector(locgService, state => state.matches('auth'))

  const { scanDestinationSectionStyles, codeDesignSectionStyles, authSectionStyles } =
    useLOCGSectionTransitionStyles(
      rootContainerRef.current ? `${rootContainerRef.current.clientHeight}px` : '100vh'
    )

  return (
    <Stack
      sx={{
        height: '100%',
        overflowY: 'auto',
        scrollBehavior: 'smooth',
        // transition styles
        position: 'relative',
        ...scanDestinationSectionStyles,
        ...codeDesignSectionStyles,
        ...authSectionStyles
      }}
      ref={rootContainerRef}
      id={SCROLL_CONTAINER_ID}
    >
      <TransitionGroup component={null}>
        {displayScanDestination && (
          <CSSTransition
            timeout={TRANSITION_DURATION}
            classNames={LoggedOutGeneratorSection.ScanDestination}
            nodeRef={scanDestinationRef}
          >
            <Stack
              id={LoggedOutGeneratorSection.ScanDestination}
              ref={scanDestinationRef}
              gap={2}
              sx={theme => ({
                alignItems: {
                  md: 'flex-start'
                },
                ml: { xs: 6, md: 8 },
                mr: { xs: 6, md: 8, xl: 0 },
                pt: {
                  xs: 5,
                  md: 'unset'
                },
                pb: 32.5,
                width: {
                  lg: '100%'
                },
                maxWidth: {
                  md: theme.spacing(136)
                }
              })}
            >
              <ScanDestination />
            </Stack>
          </CSSTransition>
        )}

        {displayCodeDesign && (
          <CSSTransition
            timeout={TRANSITION_DURATION}
            classNames={LoggedOutGeneratorSection.CodeDesign}
            nodeRef={codeDesignContainerRef}
          >
            <Stack
              id={LoggedOutGeneratorSection.CodeDesign}
              ref={codeDesignContainerRef}
              gap={2}
              alignItems="flex-start"
              sx={{
                ml: { xs: 6, md: 8 },
                mr: { xs: 6, md: 8, xl: 0 },
                pt: {
                  xs: 5,
                  md: 2
                },
                pb: {
                  xs: 20,
                  md: 40
                }
              }}
            >
              <CodeDesign ref={codeDesignRefs} />
            </Stack>
          </CSSTransition>
        )}

        {displayAuthForm && (
          <CSSTransition
            timeout={TRANSITION_DURATION}
            classNames={LoggedOutGeneratorSection.SignUp}
            nodeRef={authRef}
          >
            <Stack
              id={LoggedOutGeneratorSection.SignUp}
              ref={authRef}
              gap={2}
              alignItems="flex-start"
              sx={{
                ml: 8,
                mt: { xs: 5, md: 0 },
                mr: { xs: 8, md: 6, xl: 0 },
                pb: {
                  xs: 5,
                  lg: 13
                }
              }}
            >
              <AuthSection />
            </Stack>
          </CSSTransition>
        )}
      </TransitionGroup>
    </Stack>
  )
}
